import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../api';
import { motion, useInView, useAnimation } from 'framer-motion';
import anime from 'animejs/lib/anime.es.js';
import './Home.css';
import { MagneticButton } from '../components/MagneticButton';
import Footer from '../components/Footer';
import CookieConsent from '../components/CookieConsent';
import prof1 from '../assets/prof1.png';
import prof2 from '../assets/prof2.png';
import prof3 from '../assets/prof3.png';
import prof4 from '../assets/prof4.png';
import prof5 from '../assets/prof5.png';
import prof6 from '../assets/prof6.png';
import prof7 from '../assets/prof7.png';
import prof8 from '../assets/prof8.png';
import prof9 from '../assets/prof9.png';
import prof10 from '../assets/prof10.png';
import uni1 from '../assets/uni1.png';
import uni2 from '../assets/uni2.png';
import uni3 from '../assets/uni3.png';
import stud1 from '../assets/stud1.png';
import stud2 from '../assets/stud2.png';
import { FaLinkedin, FaInstagram, FaFacebook, FaSquareXTwitter } from "react-icons/fa6";
import {
  Briefcase,
  GraduationCap,
  Compass,
  Buildings,
  Globe,
  Handshake,
  Lightbulb,
  RocketLaunch,
  ShieldCheck,
  CalendarCheck,
  Diamond,
  Scales,
  Books,
  Heart,
  CheckCircle,
  Certificate,
  Lightning,
  MagnifyingGlass,
  FileText,
  CreditCard,
  UserCircle,
  CurrencyInr,
  Monitor,
  Video
} from '@phosphor-icons/react';

const AnimatedCounter = ({ end, duration = 2000, suffix = "" }) => {
  const ref = useRef(null);
  // Changed once: false to re-trigger every time
  const isInView = useInView(ref, { once: false, margin: "-100px" });

  useEffect(() => {
    if (isInView) {
      const counter = { val: 0 };
      anime({
        targets: counter,
        val: end,
        round: 1,
        easing: 'easeOutExpo',
        duration: duration,
        update: function () {
          if (ref.current) ref.current.innerHTML = counter.val + suffix;
        }
      });
    }
  }, [isInView, end, duration, suffix]);

  return <span ref={ref}>0{suffix}</span>;
};

// ================= ANIMATION VARIANTS =================
const springUp = {
  hidden: { opacity: 0, y: 50, scale: 0.9, rotateX: 15 }, // added rotation for 3D effect
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    rotateX: 0,
    transition: { type: "spring", stiffness: 100, damping: 20, mass: 1 } // Refined spring
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.2 }
  }
};


const floatAnim = {
  animate: {
    y: [0, -15, 0],
    transition: {
      duration: 3,
      ease: "easeInOut",
      repeat: Infinity,
    }
  }
};

const jellyHover = {
  scale: 1.05,
  transition: { type: "spring", stiffness: 300, damping: 10 }
};

const SplitText = ({ text, className, delay = 0, enableColorAnimate = true, stagger = 0.025 }) => {
  const words = text.split(" ");
  const controls = useAnimation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    let isMounted = true;
    const animateLoop = async () => {
      // Helper to safely animate
      const safeStart = async (variant) => {
        try {
          if (isMounted) await controls.start(variant);
        } catch (e) {
          // Ignore Framer Motion unmount invariant errors
        }
      };

      if (isInView) {
        await safeStart("visible");

        while (isMounted) {
          // Wait 3s
          await new Promise(r => setTimeout(r, 3000));
          if (!isMounted) break;

          await safeStart("wave");
          if (!isMounted) break;

          await safeStart("visible");
        }
      }
    };

    animateLoop();

    return () => {
      isMounted = false;
      controls.stop(); // Stop any running animation
    };
  }, [controls, isInView]);

  const wordVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 150, damping: 10 }
    },
    wave: {
      y: [0, -8, 0], // Reduced movement for professional look
      // Only animate color if allowed (avoids gradient conflict)
      ...(enableColorAnimate ? { color: ["#ffffff", "#38bdf8", "#ffffff"] } : {}),
      transition: { duration: 0.8, ease: "easeInOut" }
    }
  };

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: stagger, delayChildren: delay } },
    wave: { transition: { staggerChildren: stagger } } // Uses prop now for consistent sync
  };

  return (
    <motion.span
      ref={ref}
      className={className}
      variants={containerVariants}
      initial="hidden"
      animate={controls}
      style={{ display: "inline-block" }}
    >
      {words.map((word, i) => (
        <motion.span
          key={i}
          variants={wordVariants}
          style={{ display: "inline-block", marginRight: "0.25em" }}
        >
          {word}
        </motion.span>
      ))}
    </motion.span>
  );
};


function Home() {
  const [activeMode, setActiveMode] = React.useState(0);
  const [activeFeature, setActiveFeature] = React.useState(0); // For the new tabbed section

  const handleSpotlightMove = (e) => {
    const { currentTarget, clientX, clientY } = e;
    const { left, top } = currentTarget.getBoundingClientRect();
    const x = clientX - left;
    const y = clientY - top;
    currentTarget.style.setProperty("--x", `${x}px`);
    currentTarget.style.setProperty("--y", `${y}px`);
  };

  const engagementModes = [
    {
      title: "Professor to University",
      detail: "Experts enhancing academic value through curriculum design, guest lectures, and department strategy.",
      icon: <Briefcase size={64} weight="duotone" />
    },
    {
      title: "Student to Professor",
      detail: "Aspiring individuals connecting with industry leaders for mentorship, career guidance, and live projects.",
      icon: <GraduationCap size={64} weight="duotone" />
    },
    {
      title: "Professor to Student",
      detail: "Practitioners delivering hands-on training, capstone project supervision, and internship referrals.",
      icon: <Compass size={64} weight="duotone" />
    },
    {
      title: "University to Professor",
      detail: "Institutions actively recruiting vetted experts for adjunct roles, advisory boards, and research innovation.",
      icon: <Buildings size={64} weight="duotone" />
    }
  ];

  // Background Shape Animation
  useEffect(() => {
    anime({
      targets: '.shape.circle-1',
      translateX: [0, 80, 0], // Increased rang
      translateY: [0, 50, 0],
      scale: [1, 1.4, 1],
      easing: 'easeInOutSine',
      duration: 5000, // Faster
      loop: true
    });

    anime({
      targets: '.shape.circle-2',
      translateX: [0, -60, 0],
      translateY: [0, -80, 0],
      scale: [1, 0.7, 1],
      easing: 'easeInOutQuad',
      duration: 7000,
      loop: true,
      direction: 'alternate'
    });

    anime({
      targets: '.shape.circle-3',
      translateX: [0, 50, 0],
      translateY: [0, -40, 0],
      rotate: '1turn',
      easing: 'linear',
      duration: 10000,
      loop: true
    });
  }, []);

  // Homepage Content State
  const [pageContent, setPageContent] = React.useState({
    hero: {
      title: "Connect Academic Excellence With Industry Experience",
      subtitle: "An institutional capability platform supporting UGC-aligned Professor of Practice and industry-integration initiatives for higher education institutions."
    },
    ecosystem: {
      title: "PoP Ecosystem",
      content: {
        cards: [
          { title: "PoP Network", desc: "A thriving community of vetted experts and academic leaders.", icon: "globe", border: "blue" },
          { title: "PoP Academy", desc: "Specialized certification programs to bridge industry and academia.", icon: "certificate", border: "purple" },
          { title: "PoP Exchange", desc: "Seamless institutional engagement and talent mobility.", icon: "handshake", border: "mint" },
          { title: "PoP Insights", desc: "Cutting-edge research and thought leadership from the field.", icon: "lightbulb", border: "orange" }
        ]
      }
    },
    why_choose: {
      title: "Why Choose Our Platform",
      subtitle: "A premium ecosystem engineered for trust, security, and academic impact.",
      content: {
        benefits: [
          { title: "Rigorous Vetting", text: "Multi-layer credential verification and peer-led academic reviews.", icon: "shield", border: "mint" },
          { title: "Flexible Engagements", text: "From single guest lectures to semester-long adjunct roles.", icon: "calendar", border: "blue" },
          { title: "Transparency", text: "Data-driven credibility scores based on verified student feedback.", icon: "diamond", border: "purple" },
          { title: "Regulatory Compliance", text: "Fully aligned with NEP, UGC, and NAAC frameworks for institutional standards.", icon: "scales", border: "green" },
          { title: "Curriculum Alignment", text: "Experts matched precisely to your syllabus and learning outcomes.", icon: "books", border: "orange" },
          { title: "Social Impact", text: "Empowering the next generation with real-world industry perspectives.", icon: "heart", border: "pink" }
        ]
      }
    }
  });

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await api.get('/homepage/content');
        if (res.data && res.data.length > 0) {
          const contentMap = {};
          res.data.forEach(section => {
            contentMap[section.section_key] = section;
          });

          // Merge with defaults to ensure structure
          setPageContent(prev => ({
            ...prev,
            ...contentMap
          }));
        }
      } catch (err) {
        console.error("Failed to fetch homepage content:", err);
      }
    };
    fetchContent();
  }, []);

  // Icon Mapper Helper
  const getIcon = (iconName, props = {}) => {
    if (!iconName) return <Globe {...props} />;

    const icons = {
      globe: <Globe {...props} />,
      certificate: <Certificate {...props} />,
      handshake: <Handshake {...props} />,
      lightbulb: <Lightbulb {...props} />,
      shield: <ShieldCheck {...props} />,
      calendar: <CalendarCheck {...props} />,
      diamond: <Diamond {...props} />,
      scales: <Scales {...props} />,
      books: <Books {...props} />,
      heart: <Heart {...props} />,
      briefcase: <Briefcase {...props} />,
      graduation: <GraduationCap {...props} />,
      compass: <Compass {...props} />,
      buildings: <Buildings {...props} />,
      rocket: <RocketLaunch {...props} />,
      check: <CheckCircle {...props} />,
      lightning: <Lightning {...props} />,
      search: <MagnifyingGlass {...props} />,
      file: <FileText {...props} />,
      credit: <CreditCard {...props} />,
      user: <UserCircle {...props} />,
      rupee: <CurrencyInr {...props} />,
      monitor: <Monitor {...props} />,
      video: <Video {...props} />
    };
    return icons[iconName.toLowerCase()] || <Globe {...props} />;
  };

  // Image Mapper Helper
  const imageMap = {
    prof1, prof2, prof3, prof4, prof5, prof6, prof7, prof8, prof9, prof10,
    uni1, uni2, uni3,
    stud1, stud2
  };

  const getImg = (src) => {
    if (!src) return prof1;
    if (src.startsWith('http')) return src;
    return imageMap[src] || prof1;
  };

  return (
    <div className="landing-wrapper">
      <CookieConsent />

      {/* ================= HERO ================= */}
      <section className="landing-hero">
        <div className="hero-overlay"></div>

        {/* Anime.js Background Shapes */}
        <div className="hero-shapes-container">
          <div className="shape circle-1"></div>
          <div className="shape circle-2"></div>
          <div className="shape circle-3"></div>
        </div>

        <motion.div
          className="container hero-content"
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
        >
          <h1 className="landing-title">
            {/* If title has a " With " or " with ", we split it to keep the gradient style on the second part. 
                Otherwise just render the whole title. This is a simple heuristic. */}
            {(pageContent.hero?.title || "").toLowerCase().includes(" with ") ? (
              <>
                <SplitText text={(pageContent.hero?.title || "").split(/ with /i)[0]} /> <br />
                <span className="text-gradient">
                  <SplitText text={"With " + (pageContent.hero?.title || "").split(/ with /i)[1]} stagger={0} enableColorAnimate={false} />
                </span>
              </>
            ) : (
              <SplitText text={pageContent.hero?.title || "Connect Academic Excellence"} />
            )}
          </h1>

          <motion.p className="landing-subtitle" variants={springUp}>
            {pageContent.hero.subtitle}
          </motion.p>

          <motion.div variants={springUp} style={{ perspective: "1000px" }}>
            <div className="hero-actions" style={{ display: 'flex', gap: '30px', justifyContent: 'center' }}>
              <MagneticButton>
                <Link to="/register" className="cta-button primary-glow">
                  Get Started
                </Link>
              </MagneticButton>

              <MagneticButton>
                <Link to="/browse-professors" className="cta-button secondary-glow">
                  Browse Experts
                </Link>
              </MagneticButton>
            </div>
          </motion.div>
        </motion.div>
      </section>

      {/* ================= POP ECOSYSTEM (New Overlay) ================= */}
      <section className="pop-ecosystem-section">
        <div className="container">
          <motion.div
            className="ecosystem-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {(pageContent.ecosystem?.content?.cards || []).map((item, index) => (
              <motion.div
                className="ecosystem-card"
                key={index}
                variants={springUp}
                whileHover={jellyHover}
                onMouseMove={handleSpotlightMove}
              >
                <div className={`ecosystem-icon-bg ${item.border || 'blue'}-glow`}></div>
                <div className="ecosystem-icon">{getIcon(item.icon, { size: 40, weight: "duotone" })}</div>
                <h3>{item.title}</h3>
                <p>{item.desc || item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= FEATURES TABBED SECTION ================= */}
      <section className="features-section">
        <div className="container">
          <div className="features-tabs-wrapper">
            {/* Tab Headers */}
            <div className="features-tabs">
              {['Institutions', 'Students', 'Professionals'].map((tab, i) => (
                <button
                  key={i}
                  className={`feature-tab-btn ${activeFeature === i ? 'active' : ''}`}
                  onClick={() => setActiveFeature(i)}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="feature-tab-content">
              <motion.div
                key={activeFeature} // Triggers animation on change
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3 }}
                className="feature-content-layout"
              >
                {[
                  {
                    icon: <Buildings size={64} weight="duotone" />,
                    title: "Institutions",
                    desc: "Access vetted industry experts to enhance your curriculum and deliver real-world insights.",
                    list: ["Rigorous vetting", "NEP Aligned", "UGC & NAAC Aligned"]
                  },
                  {
                    icon: <RocketLaunch size={64} weight="duotone" />,
                    title: "Students",
                    desc: "Learn directly from industry leaders through personalized mentorship sessions and workshops.",
                    list: ["Easy Industry Exposure", "Industry Relevant Programs", "Internships & Apprenticeships"]
                  },
                  {
                    icon: <Briefcase size={64} weight="duotone" />,
                    title: "Professionals",
                    desc: "Share your expertise, mentor the next generation, and establish your academic presence.",
                    list: ["Set your schedule", "Flexible engagements", "Build Academic Career"]
                  }
                ][activeFeature].type = undefined} {/* Hack to execute logic, but we need to render the object properties */}

                {/* Actually rendering the selected feature */}
                {(() => {
                  const features = [
                    {
                      icon: <Buildings size={64} weight="duotone" />,
                      title: "Partner with Excellence",
                      desc: "Transform your academic offering by integrating top-tier industry practitioners directly into your curriculum. We facilitate seamless collaboration, letting you focus on delivering education that aligns with market demands.",
                      list: ["Rigorous vetting process", "NEP Aligned Framework", "UGC & NAAC Aligned"]
                    },
                    {
                      icon: <RocketLaunch size={64} weight="duotone" />,
                      title: "Learn from Leaders",
                      desc: "Bridge the gap between theory and practice with mentorship from professionals who shape the industry. Gain insider knowledge and practical skills through real-world projects, internships, and apprenticeships.",
                      list: ["Easy Industry Exposure", "Industry Relevant Programs", "Internships & Apprenticeships"]
                    },
                    {
                      icon: <Briefcase size={64} weight="duotone" />,
                      title: "Share Your Expertise",
                      desc: "Make a tangible impact on the next generation of talent while building your academic credentials. Whether through guest lectures, course reviews, or direct mentorship, your experience can shape the future of your industry.",
                      list: ["Set your own schedule", "Flexible engagement models", "Build Academic Career"]
                    }
                  ];
                  const item = features[activeFeature];

                  return (
                    <>
                      <div className="feature-text-side">
                        <div className="feature-icon-large">{item.icon}</div>
                        <h3>{item.title}</h3>
                        <p>{item.desc}</p>
                        <ul>
                          {item.list.map((li, i) => (
                            <li key={i}>{li}</li>
                          ))}
                        </ul>
                      </div>
                    </>
                  );
                })()}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= WHY CHOOSE ================= */}

      <section className="why-choose-wrapper">
        <div className="container section-container">

          <h2 className="section-title">
            {/* Simple heuristic for gradient: Split by last 2 words */}
            {(() => {
              const title = pageContent.why_choose?.title || "Why Choose Our Platform";
              const words = title.split(' ');
              if (words.length > 2) {
                const main = words.slice(0, words.length - 2).join(' ');
                const gradient = words.slice(words.length - 2).join(' ');
                return <>{main} <span className="text-gradient">{gradient}</span></>;
              }
              return title;
            })()}
          </h2>
          <p className="section-subtitle">{pageContent.why_choose.subtitle}</p>


          <motion.div
            className="benefits-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {(pageContent.why_choose?.content?.benefits || []).map((benefit, index) => (
              <motion.div
                key={index}
                className="benefit-item"
                variants={springUp}
                whileHover={jellyHover}
                onMouseMove={handleSpotlightMove}
              >
                <div className="benefit-icon-wrapper" style={{ background: 'transparent' }}>
                  {/* Removed old glow bg to let spotlight shine, implemented in CSS properly */}
                  <div className={`benefit-icon-bg ${benefit.border || 'blue'}-glow`}></div>
                  <div className="benefit-icon-inner">{getIcon(benefit.icon, { size: 32, weight: "duotone" })}</div>
                </div>
                <h4>{benefit.title}</h4>
                <p>{benefit.text}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ================= BROWSE PROFESSORS SECTION REMOVED FROM HOME ================= */}

      {/* ================= UNI WORKFLOW (Dark) ================= */}
      <section className="uni-workflow-section">
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div className="section-header-center dark-mode-text" style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="ext-title light text-center">University <span className="text-gradient">Pipeline</span></h2>
            <p className="section-subtitle-dark">Streamlined acquisition of top-tier industry talent.</p>
          </div>

          <div className="uni-pipeline-viz">
            <div className="pipeline-line-dark"></div>
            {[
              { title: "Define Needs", desc: "Post verified academic requirements for lectures or courses.", icon: <FileText size={24} /> },
              { title: "Smart Match", desc: "AI curates vetted experts matching your specific domain.", icon: <Lightning size={24} /> },
              { title: "Direct Connect", desc: "Chat directly with experts to align on goals.", icon: <Handshake size={24} /> },
              { title: "Execution", desc: "Automated delivery, tracking, and compliance logs.", icon: <Monitor size={24} /> }
            ].map((step, i) => (
              <motion.div
                className="pipeline-node"
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.15 }}
              >
                <div className="node-circle-dark">{step.icon}</div>
                <div className="node-content-dark">
                  <h4>{step.title}</h4>
                  <span>{step.desc}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= POP WORKFLOW (Light) ================= */}
      <section className="pop-workflow-section">
        <div className="container" style={{ maxWidth: '1200px' }}>
          <div className="section-header-center" style={{ textAlign: 'center', marginBottom: '60px' }}>
            <h2 className="section-title">Professor <span className="text-gradient">Journey</span></h2>
            <p className="section-subtitle">Monetize your expertise and build academic eminence.</p>
          </div>

          <div className="pop-ladder-viz">
            {[
              { title: "Create Profile", desc: "Showcase your corporate portfolio and academic interests.", icon: <UserCircle size={32} /> },
              { title: "Get Vetted", desc: "Complete the screening process to verify credentials and expertise.", icon: <Certificate size={32} /> },
              { title: "Set Availability", desc: "Define your hours for visiting or adjunct roles.", icon: <CalendarCheck size={32} /> },
              { title: "Teach & Earn", desc: "Deliver sessions and receive secure, automated payouts.", icon: <CurrencyInr size={32} /> }
            ].map((step, i) => (
              <motion.div
                className="ladder-step"
                key={i}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: i * 0.15 }}
              >
                <span className="ladder-num">0{i + 1}</span>
                <div className="ladder-card">
                  <div className="ladder-icon-wrapper">
                    {step.icon}
                  </div>
                  <h4>{step.title}</h4>
                  <p className="ladder-desc">{step.desc}</p>
                </div>
                {i < 3 && <div className="ladder-connector"></div>}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= ENGAGEMENT MODES ================= */}
      {false && (
        <section className="engagement-modes">
          <div className="container section-container">
            <div className="section-container">
              <h2 className="ext-title light text-center">Ways to <span className="text-gradient">Collaborate</span></h2><br />
              <p className="section-subtitle text-center">Tailored engagement models for every academic need.</p>
            </div>

            <div className="engagement-split-layout">

              {/* LEFT: Menu / List */}
              <div className="engagement-menu">
                {engagementModes.map((mode, i) => (
                  <div
                    key={i}
                    className={`engagement-menu-item ${activeMode === i ? 'active' : ''}`}
                    onMouseEnter={() => setActiveMode(i)}
                  >
                    <span className="menu-number">0{i + 1}</span>
                    <h3>{mode.title}</h3>
                  </div>
                ))}
              </div>

              {/* RIGHT: Preview Pane */}
              <div className="engagement-preview">
                <motion.div
                  key={activeMode} // Re-animates when mode changes
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  className="preview-content"
                >
                  <div className="preview-icon-large">
                    <motion.div variants={floatAnim} animate="animate">
                      {engagementModes[activeMode].icon}
                    </motion.div>
                  </div>
                  <h3>{engagementModes[activeMode].title}</h3>
                  <p>{engagementModes[activeMode].detail}</p>
                </motion.div>
              </div>

            </div>
          </div>
        </section>
      )}


      {/* ================= IMPACT BENTO GRID ================= */}
      {false && (
        <section className="impact-section">
          <div className="container section-container">
            <h2 className="section-title">Our <span className="text-gradient">Impact So Far</span></h2>

            <motion.div
              className="impact-bento-grid"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: false, margin: "-100px" }}
              variants={staggerContainer}
            >

              {/* CARD 1: Standard */}
              <motion.div className="impact-card bento-pink" variants={springUp} whileHover={jellyHover}>
                <div className="bento-header">
                  <span className="bento-badge" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><ShieldCheck weight="duotone" /> Verified</span>
                </div>
                <div className="bento-body">
                  <h3><AnimatedCounter end={2} suffix="X" /></h3>
                  <p>Better conversion with <strong>Verified Experts</strong></p>
                </div>
              </motion.div>

              {/* CARD 2: Standard with mini-tags */}
              <motion.div className="impact-card bento-purple" variants={springUp} whileHover={jellyHover}>
                <div className="bento-body">
                  <h3><AnimatedCounter end={40} suffix="+" /></h3>
                  <p>Institutions Onboarded</p>
                  <div className="bento-mini-tags">
                    <span>5/5 "Great value"</span>
                    <span>"Best experts"</span>
                  </div>
                </div>
              </motion.div>

              {/* CARD 3: TALL (Uneven height) */}
              <motion.div className="impact-card bento-green tall" variants={springUp} whileHover={jellyHover}>
                <div className="bento-header">
                  <motion.span className="bento-icon-large" variants={floatAnim} animate="animate" style={{ display: "inline-block" }}><Globe size={64} weight="duotone" /></motion.span>
                </div>
                <div className="bento-body">
                  <h3><AnimatedCounter end={5000} suffix="+" /></h3>
                  <p>Students Impacted across <strong>15+ Countries</strong></p>
                  <p className="bento-subtext">
                    Empowering the next generation of leaders through localized industry insights and global networking opportunities.
                  </p>
                  <p className="bento-subtext">
                    Our borderless mentorship model ensures that quality education is accessible regardless of geographic constraints.
                  </p>
                </div>
              </motion.div>

              {/* CARD 4: Standard with icons */}
              <motion.div className="impact-card bento-blue" variants={springUp} whileHover={jellyHover}>
                <div className="bento-body">
                  <h3>Instant</h3>
                  <p>Global pair-ups and mentorship sessions</p>
                  <div className="bento-status">
                    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><Lightning weight="duotone" /> Live</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}><CalendarCheck weight="duotone" /> Sync</span>
                  </div>
                </div>
              </motion.div>

              {/* CARD 5: Standard */}
              <motion.div className="impact-card bento-yellow" variants={springUp} whileHover={jellyHover}>
                <div className="bento-body">
                  <h3><AnimatedCounter end={95} suffix="%" /></h3>
                  <p>Success Rate in <strong>Direct Placements</strong></p>
                </div>
                <div className="bento-check"><CheckCircle size={32} weight="duotone" /></div>
              </motion.div>


            </motion.div>
          </div>
        </section>
      )}
      {false && (
        <section className="connect-learn-grow" style={{ position: 'relative', overflow: 'hidden' }}>
          {/* Decorative Background */}
          <motion.div
            className="slogan-bg-shape"
            animate={{
              rotate: 360,
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "linear"
            }}
          />

          <div className="container" style={{ position: 'relative', zIndex: 1 }}>
            <h2 className="section-title">
              {/* Dynamic Intro Text Splitting */}
              {(() => {
                const title = pageContent.intro?.title || "Connect. Learn. Grow.";
                // Split by period but keep non-empty parts
                const parts = title.split('.').map(s => s.trim()).filter(Boolean);

                if (parts.length === 0) return <SplitText text={title} />;

                return parts.map((part, i) => (
                  <React.Fragment key={i}>
                    {i === parts.length - 1 ? (
                      <span className="text-gradient">
                        <SplitText text={part + "."} delay={0.5 * i} />
                      </span>
                    ) : (
                      <><SplitText text={part + "."} delay={0.5 * i} /> </>
                    )}
                  </React.Fragment>
                ));
              })()}
            </h2>
            <motion.p
              className="section-subtitle"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 1.5, duration: 0.8 }}
            >
              {pageContent.intro?.subtitle ? (
                pageContent.intro.subtitle.split('\n').map((line, i) => (
                  <React.Fragment key={i}>
                    {line}<br />
                  </React.Fragment>
                ))
              ) : (
                <>
                  Bridging the gap between academic theory and industry practice.<br />
                  Empowering institutions with real-world expertise for a future-ready curriculum.
                </>
              )}
            </motion.p>
          </div>
        </section>
      )}
      {/* ================= EXPERTS =================
      <section className="ext-experts">
        <div className="section-container">
          <h2 className="ext-title light text-center">
            {(pageContent.experts?.title || "Meet Our Top Practitioners").replace("Top Practitioners", "")}
            <span className="text-gradient">Top Practitioners</span>
          </h2>
          <p className="section-subtitle text-center">{pageContent.experts?.subtitle}</p>
        </div>

        <div className="track-container">
          <div className="ext-expert-track-container">
            <div className="ext-expert-track">
              {(() => {
                const expertsList = pageContent.experts?.content?.experts || [
                  { name: 'Prof. Sarah Thompson', skill: 'AI Ethics', badge: 'PhD, IIT', img: 'prof1' },
                  { name: 'Dr. Michael Anderson', skill: 'Cloud Architecture', badge: 'Ex-Google', img: 'prof2' },
                  { name: 'Prof. Jennifer Miller', skill: 'Cybersecurity', badge: '12+ Yrs Exp', img: 'prof3' },
                  { name: 'Prof. James Carter', skill: 'Data Science', badge: 'PhD, Stanford', img: 'prof4' },
                  { name: 'Dr. Emily Roberts', skill: 'UX Strategy', badge: 'Ex-Microsoft', img: 'prof5' }
                ];
                return [...expertsList, ...expertsList].map((e, i) => (
                  <div className="ext-expert-card" key={i}>
                    <div className="expert-avatar-wrapper">
                      <img src={getImg(e.img)} alt={e.name} className="expert-img" />
                      <div className="online-indicator"></div>
                    </div>
                    <h4>{e.name}</h4>
                    <p className="expert-skill">{e.skill}</p>
                    <span className="expert-badge">{e.badge}</span>
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      </section> */}

      {/* ================= REVIEWS =================
      <section className="ext-reviews">
        <div className="section-container">
          <h2 className="section-title text-center">
            {(pageContent.reviews?.title || "What Our Community Says").replace("Community Says", "")}
            <span className="text-gradient">Community Says</span>
          </h2>

          <motion.div
            className="ext-review-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
          >
            {(pageContent.reviews?.content?.reviews || [
              {
                text: 'This platform transformed how we bring industry experts to campus.',
                name: 'Dr. William Anderson',
                org: 'Dean, ABC University',
                img: 'uni3'
              },
              {
                text: 'Mentorship here directly shaped my career path and professional outlook.',
                name: 'Tyler Johnson',
                org: 'Final Year Student',
                img: 'stud2'
              },
              {
                text: 'The credibility checks make this platform exceptionally trustworthy.',
                name: 'Prof. Jennifer Miller',
                org: 'Senior Industry Mentor',
                img: 'prof3'
              },
              {
                text: 'Finding guest lecturers for our specialized AI course used to take weeks. Now it takes hours.',
                name: 'Dr. Amit Parekh',
                org: 'HOD, Tech Institute',
                img: 'uni1' // Fallback
              },
              {
                text: 'The flexibility to teach while maintaining my corporate role is exactly what I was looking for.',
                name: 'Dr. Emily Roberts',
                org: 'Director, FinCorp',
                img: 'prof5'
              },
              {
                text: 'Real-world insights from people actually working in the field—this is what education should be.',
                name: 'Olivia Carter',
                org: 'Graduate Student',
                img: 'stud1'
              }
            ]).map((r, i) => (
              <motion.div
                className="ext-review-card"
                key={i}
                variants={springUp}
                whileHover={jellyHover}
              >
                <div className="review-profile">
                  <img src={getImg(r.img)} alt={r.name} className="review-avatar" />
                  <div className="review-meta">
                    <h4>{r.name}</h4>
                    <span>{r.org}</span>
                  </div>
                </div>
                <div className="stars">★★★★★</div>
                <p>“{r.text}”</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section> */}
      {/* ================= FAQ ================= */}

      <section className="ext-faq">
        <div className="section-container">
          <h2 className="ext-title light text-center">Frequently Asked <span className="text-gradient">Questions</span></h2>
          <p className="section-subtitle text-center">Everything you need to know about the Professor of Practice ecosystem.</p>

          <div className="ext-faq-list">
            <details>
              <summary>How are experts verified on the platform?</summary>
              <div className="faq-content">
                <p>
                  Trust is our core pillar. Every expert undergoes a <strong>triple-layer vetting process</strong>:
                </p>
                <ul>
                  <li><strong>Credential Verification:</strong> Direct validation of academic degrees and professional certifications.</li>
                  <li><strong>Experience Validation:</strong> Cross-referencing 10+ years of industry leadership via LinkedIn and corporate records.</li>
                  <li><strong>Peer Review:</strong> A panel of existing academic leads assesses their ability to translate complex industry jargon into educational content.</li>
                </ul>
              </div>
            </details>

            <details>
              <summary>Is the platform free to use?</summary>
              <div className="faq-content">
                <p>
                  Our pricing model is designed for accessibility. Browsing our directory of world-class experts is <strong>completely free</strong> for registered institutions and students.
                </p>
                <p>
                  Financial commitments only begin when an engagement is booked. These can range from pro-bono guest lectures for non-profits to competitive market rates for semester-long adjunct roles, depending on the expert's preference and the institution's budget.
                </p>
              </div>
            </details>

            <details>
              <summary>Can institutions request specific experts?</summary>
              <div className="faq-content">
                <p>
                  Absolutely. Institutions have two pathways for recruitment:
                </p>
                <ol>
                  <li><strong>Direct Invitation:</strong> If you find an expert who fits your curriculum perfectly, you can send a direct proposal through their profile.</li>
                  <li><strong>Requirement Matching:</strong> Post a specific role (e.g., "AI Strategy Guest Lecturer"), and our AI matching engine will shortlist the top 5 vetted practitioners who meet your criteria.</li>
                </ol>
              </div>
            </details>

            <details>
              <summary>How are payments handled?</summary>
              <div className="faq-content">
                <p>
                  We utilize a <strong>secure Escrow-based payment gateway</strong> to protect both parties. Once a contract is signed, the institution deposits the funds into a neutral account.
                </p>
                <p>
                  Funds are only released to the professional after the lecture, workshop, or milestone has been successfully completed and acknowledged by the institution. This ensures 100% compliance and fair compensation.
                </p>
              </div>
            </details>

            <details>
              <summary>Can professionals set their own availability?</summary>
              <div className="faq-content">
                <p>
                  Yes. We understand that our experts are active leaders in their industries. Our platform provides a <strong>Granular Availability Dashboard</strong> where you can:
                </p>
                <ul>
                  <li>Sync your Google/Outlook calendar to prevent double-booking.</li>
                  <li>Define "Academic Windows" (e.g., only Tuesday evenings or weekend workshops).</li>
                  <li>Set different pricing tiers for one-off guest lectures vs. ongoing advisory roles.</li>
                </ul>
              </div>
            </details>
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="ext-cta">
        <div className="container">
          <motion.div
            className="cta-card"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={springUp}
          >
            <div className="cta-content">
              <h2>Start Building Academic Impact Today</h2>
              <p>Join over 5,000+ experts and 1,200 institutions reshaping the future of education.</p>
              <div className="cta-actions">
                <MagneticButton className="cta-button-wrapper">
                  <Link to="/register" className="cta-button cta-final">
                    Create Free Account
                  </Link>
                </MagneticButton>
              </div>
            </div>
            <div className="cta-visual">
              {/* This represents a decorative element or a subtle graphic */}
              <div className="floating-stats">
                <div className="stat-pill">Active Engagements: 1,240</div>
                <div className="stat-pill">Avg. Expert Rating: 4.9/5</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ================= MULTI-COLUMN FOOTER ================= */}
      <Footer />
    </div>
  );
}

export default Home;
